import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const SessionForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    sport: '',
    date: new Date()?.toISOString()?.split('T')?.[0],
    totalDuration: '',
    activeDuration: '',
    rpe: 5,
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sportOptions = [
    { value: 'rest_day', label: 'Rest Day' },
    { value: 'running', label: 'Running' },
    { value: 'cycling', label: 'Cycling' },
    { value: 'swimming', label: 'Swimming' },
    { value: 'strength', label: 'Strength Training' },
    { value: 'football', label: 'Football' },
    { value: 'basketball', label: 'Basketball' },
    { value: 'tennis', label: 'Tennis' },
    { value: 'yoga', label: 'Yoga' },
    { value: 'other', label: 'Other' }
  ];

  const rpeLabels = {
    1: 'Very Easy',
    2: 'Easy',
    3: 'Moderate',
    4: 'Somewhat Hard',
    5: 'Hard',
    6: 'Very Hard',
    7: 'Extremely Hard',
    8: 'Maximum Effort',
    9: 'Near Maximum',
    10: 'Maximum'
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.sport) {
      newErrors.sport = 'Please select a sport';
    }
    
    if (!formData?.totalDuration && formData?.totalDuration !== 0) {
      newErrors.totalDuration = 'Please enter total session duration';
    }
    
    if (!formData?.activeDuration && formData?.activeDuration !== 0) {
      newErrors.activeDuration = 'Please enter active duration';
    }
    
    const totalDuration = parseInt(formData?.totalDuration);
    const activeDuration = parseInt(formData?.activeDuration);
    const isRestDay = formData?.sport === 'rest_day';
    
    if (!isRestDay && totalDuration < 1) {
      newErrors.totalDuration = 'Total duration must be at least 1 minute';
    }
    
    if (!isRestDay && activeDuration < 1) {
      newErrors.activeDuration = 'Active duration must be at least 1 minute';
    }
    
    if (activeDuration > totalDuration) {
      newErrors.activeDuration = 'Active duration cannot exceed total duration';
    }
    
    if (totalDuration > 600) {
      newErrors.totalDuration = 'Duration cannot exceed 10 hours';
    }
    
    if (formData?.rpe < 1 || formData?.rpe > 10) {
      newErrors.rpe = 'RPE must be between 1 and 10';
    }
    
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today?.setHours(23, 59, 59, 999);
    
    if (selectedDate > today) {
      newErrors.date = 'Cannot log sessions for future dates';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-populate fields when Rest Day is selected
      if (field === 'sport' && value === 'rest_day') {
        updated.totalDuration = 0;
        updated.activeDuration = 0;
        updated.rpe = 1;
      }
      
      return updated;
    });
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleRPEChange = (value) => {
    setFormData(prev => ({
      ...prev,
      rpe: parseInt(value)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const totalDuration = parseInt(formData?.totalDuration);
      const activeDuration = parseInt(formData?.activeDuration);
      const sessionData = {
        ...formData,
        duration: totalDuration,
        activeDuration: activeDuration,
        load: activeDuration * formData?.rpe,
        timestamp: new Date()?.toISOString()
      };
      
      await onSubmit(sessionData);
      
      // Reset form after successful submission
      setFormData({
        sport: '',
        date: new Date()?.toISOString()?.split('T')?.[0],
        totalDuration: '',
        activeDuration: '',
        rpe: 5,
        notes: ''
      });
      
    } catch (error) {
      console.error('Error submitting session:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Plus" size={18} color="white" strokeWidth={2} />
          </div>
          <h2 className="text-xl font-semibold text-text-primary">Log Training Session</h2>
        </div>
        
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={16} color="var(--color-text-secondary)" strokeWidth={2} />
          <span className="text-sm text-text-secondary">
            {new Date()?.toLocaleDateString('en-GB', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long' 
            })}
          </span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sport and Date Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Sport/Activity"
            options={sportOptions}
            value={formData?.sport}
            onChange={(value) => handleInputChange('sport', value)}
            error={errors?.sport}
            required
            searchable
            placeholder="Select or search sport"
          />
          
          <Input
            label="Session Date"
            type="date"
            value={formData?.date}
            onChange={(e) => handleInputChange('date', e?.target?.value)}
            error={errors?.date}
            required
            max={new Date()?.toISOString()?.split('T')?.[0]}
          />
        </div>

        {/* Duration Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Total Session Duration (minutes)"
              type="number"
              placeholder="90"
              value={formData?.totalDuration}
              onChange={(e) => handleInputChange('totalDuration', e?.target?.value)}
              error={errors?.totalDuration}
              required
              min="0"
              max="600"
              disabled={formData?.sport === 'rest_day'}
            />
          </div>
          <div>
            <Input
              label="Active Duration (minutes)"
              type="number"
              placeholder="75"
              value={formData?.activeDuration}
              onChange={(e) => handleInputChange('activeDuration', e?.target?.value)}
              error={errors?.activeDuration}
              required
              min="0"
              max="600"
              disabled={formData?.sport === 'rest_day'}
            />
            <p className="mt-1 text-xs text-text-secondary">
              Used for load calculation (Active minutes × RPE)
            </p>
          </div>
        </div>

        {/* RPE Slider */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Rate of Perceived Exertion (RPE) <span className="text-error">*</span>
          </label>
          <div className="space-y-4">
            <div className="px-4 py-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold text-primary">{formData?.rpe}</span>
                <span className="text-sm font-medium text-text-primary">
                  {rpeLabels?.[formData?.rpe]}
                </span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={formData?.rpe}
                onChange={(e) => handleRPEChange(e?.target?.value)}
                className="w-full h-2 bg-border rounded-lg appearance-none cursor-pointer slider"
                disabled={formData?.sport === 'rest_day'}
              />
              <div className="flex justify-between text-xs text-text-secondary mt-1">
                <span>1 - Very Easy</span>
                <span>10 - Maximum</span>
              </div>
            </div>
          </div>
          {errors?.rpe && (
            <p className="mt-1 text-sm text-error">{errors?.rpe}</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Session Notes (Optional)
          </label>
          <textarea
            value={formData?.notes}
            onChange={(e) => handleInputChange('notes', e?.target?.value)}
            placeholder="Add any notes about your session..."
            rows="3"
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="Info" size={16} color="currentColor" strokeWidth={2} />
            <span className="text-sm">
              Load = Active Duration × RPE
            </span>
          </div>
          
          <Button
            type="submit"
            variant="default"
            loading={isSubmitting}
            iconName="Save"
            iconPosition="left"
            disabled={
              !formData?.sport || 
              (formData?.sport !== 'rest_day' && (!formData?.totalDuration || !formData?.activeDuration)) ||
              (formData?.sport === 'rest_day' && (formData?.totalDuration !== 0 || formData?.activeDuration !== 0))
            }
          >
            {isSubmitting ? 'Saving Session...' : 'Save Session'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SessionForm;